
import { Component, Mixins, Watch } from "vue-property-decorator";
import { AxiosError } from "axios";

import PazTooltipButton from "@pazion/pazion-core/src/components/controls/PazTooltipButton.vue";
import PazConfirmation from "@pazion/pazion-core/src/components/utils/PazConfirmation.vue";
import PazToast from "@pazion/pazion-core/src/components/utils/PazToast.vue";
import PazRefreshButton from "@pazion/pazion-core/src/components/controls/PazRefreshButton.vue";
import EntityLogDialog from "@pazion/pazion-core/src/components/utils/EntityLogDialog.vue";

import { ITablePaginationOptions } from "@pazion/pazion-catalog/src/models/base";
import { IRmaModel, IRmaPaginateModel } from "@pazion/pazion-catalog/src/models/rma.model";

import EntityLogMixin from "@pazion/pazion-core/src/mixins/EntityLog.mixin";
import RmaAttributesMixin from "./mixins/rmaAttributes.mixin";

import { getReturns, updateReturns, deleteReturn } from "@pazion/pazion-catalog/src/services/catalog-service";

@Component({
  components: {
    PazTooltipButton,
    PazRefreshButton,
    PazToast,
    PazConfirmation,
    EntityLogDialog,
  },
})
export default class AccountsListing extends Mixins(
  EntityLogMixin,
  RmaAttributesMixin
) {
  public loading: boolean = false;
  public search: string = "";
  public returnsItems = {} as IRmaPaginateModel;
  public footerProps = {
    itemsPerPageOptions: [50, 100, 200, -1],
  };
  public paginationOptions: ITablePaginationOptions = { itemsPerPage: 100 };
  public totalItems = 1;
  public selectedRma: IRmaModel | null = null;

  public headers = [
    {
      text: "rma id",
      align: "left",
      sortable: true,
      value: "id",
    },
    {
      text: "rma number",
      align: "left",
      sortable: true,
      value: "rma_number",
    },
    {
      text: "rma line id",
      align: "left",
      sortable: true,
      value: "rma_line_id",
    },
    {
      text: "order",
      align: "left",
      sortable: true,
      value: "order",
    },

    {
      text: "reference",
      align: "left",
      sortable: true,
      value: "reference",
    },
    {
      text: "return reason",
      align: "left",
      sortable: true,
      value: "reason",
    },
    {
      text: "notes",
      align: "left",
      sortable: true,
      value: "notes",
    },
    {
      text: "status",
      align: "left",
      sortable: true,
      value: "status",
    },
    {
      text: "Actions",
      align: "right",
      sortable: false,
      value: "actions",
    },
  ];

  async onRetryClick(id: string) {
    try {
      this.loading = true;
      let data = {
        id,
        status: "NEW",
      };
      const response = await updateReturns(data);
      this.$refs.toast.show({ message: `Return #${response.id} retried` });
      await this.onSearch();
    } catch (error) {
      let response = (error as AxiosError).response;
      if (response && response.status !== 500) {
        this.$refs.toast.show({
          message: response?.data?.message,
          color: "red",
          icon: "mdi-close-circle",
        });
        this.loading = false;
        return;
      }
      this.loading = false;
      this.$refs.toast.show({
        message: "An error occurred, please contact the administrator",
        icon: "mdi-close-circle",
        color: "red",
      });
    }
  }

  public defaultQueryPaginationsOpt = {
    pagination: 1,
    perPage: 50,
    page: 1,
    expand: "rmaLine,attributeValue,rmaAttribute",
  };

  private _timerId = null;

  createSearchString(search: string) {
    return search.length > 0 ? { like: `%${this.search}%` } : null;
  }

  createFiltersObject() {
    if (this.search.length > 0) {
      return {
        reference: this.createSearchString(this.search),
      };
    }
  }

  getRmaSubFiled(rma: IRmaModel, key: string, subKey?: string) {
    return (
      (rma && rma[key].length && rma[key].length > 0 && rma[key][0][subKey]) ||
      ""
    );
  }

  onSearch() {
    clearTimeout(this._timerId);
    this._timerId = setTimeout(async () => {
      const searchString = this.createFiltersObject();
      const filters = this.createFiltersObject();

      const queryParams = searchString
        ? {
            ...this.defaultQueryPaginationsOpt,
            filter: JSON.stringify({
              ...filters,
            }),
          }
        : {
            ...this.defaultQueryPaginationsOpt,
            filter: JSON.stringify({ ...filters }),
          };
      await this.fetchData(queryParams);
    }, 800);
  }

  linkToOrder(orderReference: string) {
    this.$router.push({
      name: "orders",
      query: { orderReference: orderReference },
    });
  }

  getAttributesCount(item: IRmaModel) {
    return item.attributeValue?.length;
  }

  async fetchData(
    queryParams: { [key: string]: any | { [key: string]: string } } | {}
  ) {
    const fullParams = queryParams
      ? queryParams
      : this.defaultQueryPaginationsOpt;
    try {
      this.loading = true;
      const response = await getReturns({
        ...fullParams,
      });
      if (!response) return;
      this.returnsItems = response;
      this.totalItems = response._pagination._total;
      Object.assign(this.paginationOptions, {
        page: response._pagination._page,
        itemsPerPage: response._pagination._per_page,
      });
      this.loading = false;
    } catch (error) {
      this.loading = false;
    }
  }

  // NOTE: FROM Paginastion
  @Watch("paginationOptions")
  onUpdatePaginations(val, oldVal) {
    if (
      val.itemsPerPage !== oldVal.itemsPerPage ||
      val.page !== oldVal.page ||
      val.sortBy.length > 0
    ) {
      let pagiantionParams;
      if (val.itemsPerPage > 0)
        pagiantionParams = {
          pagination: 1,
          perPage: val.itemsPerPage,
          page: val.page,
          expand: "order,rmaLine,attributeValue,rmaAttribute",
        };
      else {
        pagiantionParams = this.defaultQueryPaginationsOpt;
        pagiantionParams.perPage = 200;
      }
      let filters = this.createFiltersObject();
      filters &&
        Object.assign(pagiantionParams, { filter: JSON.stringify(filters) });
      this.fetchData(pagiantionParams);
    }
  }
  async deleteReturns(item: IRmaModel) {
    const confirm = await this.$refs.confirmDeleteReturn.openConfirmDialog(
        "Delete",
        `Do you want to delete RMA ${item.id}?`
    );
    if (confirm) {
      const rsp = await deleteReturn(item.id);
      if (rsp.id > 0) {
        this.onSearch();
      }
    }
  }

  get getReturnsArray() {
    return this.returnsItems && this.returnsItems?._data;
  }
} //
